import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, take } from 'rxjs/operators';
import { CaseStatusService } from 'src/app/diagnostics/component/alert-case/case-status.service';
import { AlertDetailService } from 'src/app/diagnostics/component/alert-detail/alert-detail.service';
import { AlertTypes } from 'src/app/models/alertTypes';
import { CaseUserStatus } from 'src/app/models/referenceData';
import { AngularMaterialModule } from 'src/app/shared/module/angular-material.module';
import { ApplicationPipesModule } from 'src/app/shared/module/applicationPipesModule';
import { AppConfigurationService } from 'src/app/shared/services/app-configuration.service';
import { AlertTypesState } from 'src/app/store/state/alert-types.state';
import { CaseRef, CaseRelation, HistoryRow, caseRelation } from './alert-history';
import { Lamp, lampSrc } from './alert-lamps';
import { FaultLine, buildFaultLines, faultCodeLabel } from './fault-lines';

@Component({
  selector: 'app-alert-history-panel',
  templateUrl: './alert-history-panel.component.html',
  styleUrl: './alert-history-panel.component.scss',
  standalone: true,
  imports: [CommonModule, AngularMaterialModule, TranslateModule, ApplicationPipesModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertHistoryPanelComponent implements OnChanges {
  @Input() rows: HistoryRow[] = [];
  @Input() set currentId(id: string | undefined) {
    this.viewingId = id;
    this.openId = null;
  }
  @Input() openCase: CaseRef | null = null;

  viewingId: string | undefined;
  openId: string | null = null;
  triggeredFaults: Record<string, Observable<FaultLine[] | 'error'>> = {};
  relations: Record<string, CaseRelation> = {};
  private statuses: CaseUserStatus[] | null = null;
  private statusesRequested = false;

  @Select(AlertTypesState.getAlertTypes) alertTypes$: Observable<AlertTypes>;

  constructor(
    private alertDetailService: AlertDetailService,
    private caseStatusService: CaseStatusService,
    private appConfig: AppConfigurationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(): void {
    this.relate();
    if (this.statusesRequested || !this.rows.some(r => r.case)) return;
    this.statusesRequested = true;
    this.caseStatusService.userStatuses().pipe(take(1)).subscribe(statuses => {
      this.statuses = statuses;
      this.relate();
      this.cdr.markForCheck();
    });
  }

  // Held until statuses load so a closed case never flashes as "Not Started".
  private relate(): void {
    this.relations = {};
    if (!this.statuses) return;
    for (const r of this.rows) this.relations[r.id] = caseRelation(r.case, this.openCase, this.statuses);
  }

  // The list endpoint carries no faults, so a firing's triggered faults load on first expand.
  toggle(row: HistoryRow): void {
    if (row.id === this.viewingId) return;
    this.openId = this.openId === row.id ? null : row.id;
    this.triggeredFaults[row.id] ??= combineLatest([
      this.alertDetailService.getData(row.id),
      this.alertTypes$.pipe(filter(t => !!t?.faultcodes?.length)),
    ]).pipe(
      take(1),
      map(([alert, alertTypes]) => buildFaultLines(alert, alertTypes).filter(l => l.triggeredAlert)),
      catchError(() => of('error' as const)),
      shareReplay(1),
    );
  }

  trackRow = (_: number, row: HistoryRow): string => row.id;

  codeLabel(fault: FaultLine): string {
    return faultCodeLabel(fault, (key, params) => this.translate.instant(key, params));
  }

  lampSrc(lamp: Lamp): string {
    return lampSrc(this.appConfig.settings?.contentDeliveryNetworkUrl ?? '', lamp);
  }
}
